import { useState, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import {
  getCachedRoleDefinitions,
  setCachedRoleDefinitions,
  type RoleDefinition,
} from '@/lib/roleDefinitionsCache';

export type { RoleDefinition };

export function useRoleDefinitions() {
  const { makeAuthenticatedCall } = useAuthenticatedApi();
  const [roleDefinitions, setRoleDefinitions] = useState<RoleDefinition[]>(
    getCachedRoleDefinitions() ?? []
  );
  const [isLoading, setIsLoading] = useState(false);

  const fetchRoleDefinitions = useCallback(async (force?: boolean) => {
    if (!force && getCachedRoleDefinitions()) {
      setRoleDefinitions(getCachedRoleDefinitions()!);
      return;
    }

    setIsLoading(true);
    try {
      const result = await makeAuthenticatedCall<{ roles: RoleDefinition[] }>('get-profile-roles');
      if (result.data?.roles && Array.isArray(result.data.roles)) {
        const definitions = result.data.roles.map((r: any) => ({
          id: Number(r.id),
          name: String(r.name ?? ''),
          capabilities: r.capabilities && typeof r.capabilities === 'object' ? r.capabilities : {},
        }));
        setCachedRoleDefinitions(definitions);
        setRoleDefinitions(definitions);
      }
    } catch (err) {
      console.error('Failed to fetch role definitions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [makeAuthenticatedCall]);

  return { roleDefinitions, isLoading, fetchRoleDefinitions };
}
